import React, { useEffect, useRef, useState } from 'react';
import styles from './AdvancedSearch.module.scss';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import {
  CmsPageQuery,
  SearchCategory,
  SearchQuery,
  SearchResultDto,
  UserQuery,
  UserRole,
} from 'services/api/api-client.ts';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import { StringParam, useQueryParams } from 'react-router-url-params';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from '../../../helpers/pagination-helper.ts';
import Cross from 'assets/icons/close-button-small.svg?react';
import SearchIcon from 'assets/icons/search-blue.svg?react';
import { useAdvancedForm } from '../../../helpers/form/useAdvancedForm.ts';
import clsx from 'clsx';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import { Input } from '../../../components/uikit/inputs/Input.tsx';
import { useWatch } from 'react-hook-form';
import {
  Button,
  ButtonColor,
} from '../../../components/uikit/buttons/Button.tsx';
import { NegativeActionText } from '../../../components/uikit/NegativeActionText.tsx';
import { Trans, useTranslation } from 'react-i18next';
import { useDataGrid } from '../../../components/devexpress/Grid/dx-grid.ts';
import { useProcessLinks } from '../../../helpers/useProcessLinks.ts';
import { LottieContainer } from '../../../components/animations/LottieContainer.tsx';
import animationData from '../../../assets/animations/loader_small.json';
import { downloadOrOpenBlobFile } from '../../../helpers/download-helper.ts';
import { ActionText } from '../../../components/uikit/ActionText.tsx';
import ArrowRight from 'assets/icons/arrow-long-right.svg?react';
import { buildUrl, highlightMatches } from '../../../helpers/search-helper.tsx';
import { Link } from 'react-router-dom';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { CustomModal } from '../../../components/uikit/modal/CustomModal.tsx';
import { Tooltip } from '../../../components/uikit/Tooltip.tsx';
import { tDynamic } from '../../../helpers/tDynamic.ts';
import {
  MultiSelectChips,
  MultiSelectOption,
} from '../../../components/uikit/inputs/MultiSelectChips.tsx';
import { FeatureFlags } from '../../../application/constants/env-variables.ts';
import {
  all,
  AllowedRoles,
  AllowedRolesValue,
} from 'application/constants/allowed-roles.ts';

export type Area = {
  value: SearchCategory;
  label: string;
  allowedRoles?: AllowedRolesValue;
};

export const areas: Area[] = [
  {
    label: 'Warranty_request_history',
    value: SearchCategory.WarrantyRequestSummary,
    allowedRoles: AllowedRoles.WarrantyRequestHistoryPage,
  },
  {
    label: 'Abas_documents',
    value: SearchCategory.AbasDocuments,
    allowedRoles: [
      ...new Set(
        [
          AllowedRoles.PriceListsPage,
          AllowedRoles.TelehealthBillingPage,
          AllowedRoles.SNLotsPage,
          AllowedRoles.TechUpdatesPage,
          AllowedRoles.ServicePresentationPage,
          AllowedRoles.ServiceAndRepairPage,
        ].flat(),
      ),
    ] as AllowedRolesValue,
  },
  {
    label: 'Cms_pages',
    value: SearchCategory.CmsPages,
  },
  {
    label: 'Order_list',
    value: SearchCategory.OrderList,
    allowedRoles: AllowedRoles.OrderListPage,
  },
  {
    label: 'Qualifications',
    value: SearchCategory.Qualifications,
    allowedRoles: AllowedRoles.QualificationsPage,
  },
  {
    label: 'Repairs',
    value: SearchCategory.Repairs,
    allowedRoles: AllowedRoles.RepairsPage,
  },
  {
    label: 'Outstanding_receivables',
    value: SearchCategory.OutstandingReceivables,
    allowedRoles: AllowedRoles.OutstandingReceivablesPage,
  },
];

const mail = 'supportHC@loewensteinmedical.com';
const pdf = 'pdf';

const settingsAndUse = 'settings-and-use';

const firmware = 'firmware';
const ventilation = 'ventilation';
const secretionManagement = 'secretion-management';
const accessories = 'accessories';

const software = 'software';
const leomanager = 'leomanager';
const other = 'other';

const firmwareSoftwareUrls = [
  firmware,
  ventilation,
  secretionManagement,
  accessories,
  software,
  leomanager,
  other,
];

const PAGE_SIZE = 50;
const second = 1000;

export type Form = {
  searchValue: string;
  areas: Area[];
};

export const AdvancedSearch = () => {
  const i18n = useScopedTranslation('Page.AdvancedSearch');
  const i18nCommon = useTranslation();

  const [isInterruptModalVisible, setIsInterruptModalVisible] = useState(false);

  const observer = useRef<IntersectionObserver>(null);
  const counterLastCall = useRef(0);

  const openInterruptModal = () => {
    setIsInterruptModalVisible(true);
  };

  const closeInterruptModal = () => {
    setIsInterruptModalVisible(false);
  };

  const [params, setParams] = useQueryParams({
    searchValue: StringParam,
    areas: StringParam,
    ...pagingSortingQueryParams(PAGE_SIZE),
  });

  const {
    addProcessingLink,
    removeProcessingLink,
    processingLinks,
    showTooltip,
    isTooltipVisible,
    closeTooltip,
  } = useProcessLinks();

  useDataGrid();

  const userQuery = UserQuery.useGetCurrentUserInfoQuery();
  const userRoles = userQuery.data?.claimRoles ?? [];
  const isAdmin = userRoles.includes(UserRole.Admin);

  const inaccessibleUrlsQuery =
    CmsPageQuery.useGetInaccessiblePublishedUrlsQuery({
      retry: false,
      throwOnError: false,
      enabled: !isAdmin,
      staleTime: 3 * 60 * 1000,
    });

  const areasFilteredByRoles = areas.filter((area) => {
    const allowedRoles = area.allowedRoles;

    if (allowedRoles === all) {
      return true;
    }

    if (
      !!allowedRoles &&
      allowedRoles.some((role) => userRoles.includes(role))
    ) {
      return true;
    }

    // abas documents category includes Settings and use page which is CMS page
    // that's why we need to check if user has access to this page
    if (
      area.value === SearchCategory.AbasDocuments &&
      !inaccessibleUrlsQuery.data?.includes(settingsAndUse)
    ) {
      return true;
    }

    return (
      area.value === SearchCategory.CmsPages &&
      firmwareSoftwareUrls.some(
        (urlString) => !inaccessibleUrlsQuery.data?.includes(urlString),
      )
    );
  });

  const getAreasFromParams = () => {
    const res: Area[] = [];

    if (!params.areas) {
      return [];
    }

    const keys = params.areas.split('_');

    for (const key of keys) {
      const area = areasFilteredByRoles.find((area) => area.value === key);

      if (area) {
        res.push({
          value: area.value,
          label: area.label,
        });
      }
    }

    return res;
  };

  const form = useAdvancedForm<Form>(
    async (data) => {
      setParams({
        ...params,
        perPage: PAGE_SIZE,
        searchValue: data.searchValue,
        areas: data.areas.map((area) => area.value).join('_'),
      });
    },
    {
      defaultValues: {
        searchValue: params.searchValue ?? '',
        areas: getAreasFromParams(),
      },
    },
  );

  const [searchValue, selectedAreas] = useWatch({
    control: form.control,
    name: ['searchValue', 'areas'],
  });

  const setSelectedAreas = (value: MultiSelectOption[]) => {
    form.setValue('areas', value as Area[]);
  };

  const searchData = SearchQuery.useAdvancedSearchQuery(
    {
      search: params.searchValue ?? undefined,
      categories: getAreasFromParams().map((area) => area.value),
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      placeholderData: (previousData) => {
        if (params.perPage > PAGE_SIZE) {
          return previousData;
        }
      },
      enabled: !!params.searchValue,
    },
  );

  const resetFilters = () => {
    form.reset({
      searchValue: '',
      areas: [],
    });

    setParams({
      ...params,
      searchValue: '',
      areas: '',
    });
  };

  const hasMore = !!(
    searchData.data?.totalCount && searchData.data?.totalCount > params.perPage
  );

  const loadMore = () => {
    const now = Date.now();

    if (now - counterLastCall.current < second) {
      return;
    }

    if (!searchData.isFetching && hasMore) {
      counterLastCall.current = now;
      setParams({
        ...params,
        perPage: params.perPage + PAGE_SIZE,
      });
    }
  };

  useEffect(() => {
    if (!searchData.isFetching) {
      closeInterruptModal();
    }
  }, [searchData.isFetching]);

  const isResetButtonShow = !!params.searchValue;
  const isPlaceholderVisible =
    (!searchData.data || !searchData.data.totalCount) && !searchData.isFetching;

  const lastRowRef = (node: HTMLElement | null) => {
    if (searchData.isFetching) {
      return;
    }

    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMore();
      }
    });

    if (node) {
      observer.current.observe(node);
    }
  };

  const getLinkToPage = (pageInfo: SearchResultDto) => {
    let url = buildUrl(pageInfo);

    if (!url.startsWith('/')) {
      url = '/' + url;
    }

    return url;
  };

  const isWarningMessageNeeded =
    FeatureFlags.isTodayStage() ||
    FeatureFlags.isGreenStage() ||
    FeatureFlags.isCareStage();

  return (
    <div data-test-id={'advanced-search-page'}>
      <Breadcrumbs />
      <form onSubmit={form.handleSubmitDefault}>
        <div
          className={clsx(
            styles.rowContainer,
            styles.columnGap24,
            styles.marginBottom32,
          )}
        >
          <div
            className={clsx(
              styles.rowContainer,
              styles.columnGap8,
              styles.relative,
            )}
          >
            <div className={styles.greyText}>{i18n.t('search_in_leo')}</div>
            <div className={clsx(styles.relative)}>
              <SearchIcon className={styles.searchIcon} />
              <Input
                {...form.register('searchValue')}
                testId={'search-input'}
                className={clsx(styles.width368, styles.paddingLeft40)}
                endAdornment={
                  <div>
                    {searchValue && (
                      <IconButton
                        type={'destroy'}
                        onClick={() => form.setValue('searchValue', '')}
                      >
                        <Cross />
                      </IconButton>
                    )}
                  </div>
                }
                endAdornmentClassname={styles.endAdornmentClassname}
              />
            </div>
          </div>
          <div className={clsx(styles.rowContainer, styles.columnGap8)}>
            <div className={styles.greyText}>{i18n.t('area')}</div>
            <MultiSelectChips
              options={areasFilteredByRoles as MultiSelectOption[]}
              selected={selectedAreas as MultiSelectOption[]}
              onChange={setSelectedAreas}
              getOptionLabel={(option) => tDynamic(i18n, option.label)}
              chooseLabel={i18n.t('choose_area')}
              selectAllLabel={i18n.t('select_all')}
              moreLabel={i18n.t('more')}
            />
          </div>
          <Button
            type={searchData.isFetching ? 'button' : 'submit'}
            className={clsx(styles.actionButton, styles.marginBottomMinus4)}
            title={i18n.t('apply')}
            onClick={searchData.isFetching ? openInterruptModal : undefined}
            testId={'filters-apply-button'}
          />
          {isResetButtonShow && (
            <NegativeActionText
              text={i18n.t('reset')}
              onClickHandler={resetFilters}
              containerClassName={styles.marginBottomMinus4}
              testId={'filters-reset-button'}
            />
          )}
        </div>
      </form>
      <Loading
        loading={
          searchData.isLoading ||
          (searchData.isPlaceholderData && searchData.isFetching)
        }
        title={i18n.t('loader_text')}
        className={clsx(
          styles.loading,
          isWarningMessageNeeded && styles.loadingDev,
        )}
        textClassName={clsx(styles.loaderText)}
        testId={'loader'}
      >
        {searchData.data?.data.map((item, index) => (
          <div
            data-test-id={'result-container'}
            key={item.pageName}
            className={clsx(
              index + 1 !== searchData.data?.data.length &&
                styles.marginBottom32,
            )}
          >
            <div
              data-test-id={'page-name'}
              className={clsx(styles.title, styles.borderBottom)}
            >
              {item.pageName}
            </div>
            {item.searchResults.map((searchResult) => {
              const url = getLinkToPage(searchResult);
              const fileLink = searchResult.fileLink;
              const fileName = fileLink?.slice(0, fileLink?.lastIndexOf('.'));
              const extension = fileLink?.slice(fileLink?.lastIndexOf('.') + 1);

              const downloadLink = `download-${fileLink}`;
              const openLink = `open-${fileLink}`;
              const isDownloadLoading = processingLinks.has(downloadLink);
              const isOpenLoading = processingLinks.has(openLink);

              return (
                <div
                  key={`${searchResult.pageName}-${
                    searchResult.foundSearchValue ?? ''
                  }-${searchResult.fileLink ?? ''}-${
                    searchResult.matchedFields['FileGroupName'] ?? ''
                  }`}
                  className={styles.dataRow}
                >
                  <div className={clsx(styles.rowContainer, styles.width100)}>
                    <div
                      data-test-id={'found-value'}
                      className={clsx(styles.searchTitle)}
                    >
                      {highlightMatches(
                        searchResult.foundSearchValue ?? searchResult.pageName,
                        params.searchValue ?? '',
                        styles.highlight,
                      )}
                    </div>
                    <div className={styles.flex1}>
                      <Link
                        data-test-id={'link-to-page'}
                        to={url}
                        target={'_blank'}
                        relative={'path'}
                        className={styles.link}
                      >
                        <ActionText
                          className={styles.underline}
                          text={i18n.t('go_to_page')}
                        />
                      </Link>
                    </div>
                    <div className={styles.rowContainer}>
                      <div className={styles.width136}>
                        {fileLink ? (
                          isDownloadLoading ? (
                            <div className={styles.height40}>
                              <LottieContainer
                                animationData={animationData}
                                segments={
                                  isDownloadLoading ? [1, 180] : [180, 270]
                                }
                                loop={isDownloadLoading}
                                width={28}
                                height={28}
                              />
                            </div>
                          ) : (
                            <div>
                              <Button
                                testId={'download-button'}
                                className={clsx(styles.actionButton)}
                                title={i18nCommon.t('download')}
                                onClick={() => {
                                  addProcessingLink(downloadLink);
                                  downloadOrOpenBlobFile(fileLink, fileName)
                                    .catch(showTooltip)
                                    .finally(() =>
                                      removeProcessingLink(downloadLink),
                                    );
                                }}
                              />
                              <div className={styles.caption}>{extension}</div>
                            </div>
                          )
                        ) : null}
                      </div>
                      <div className={styles.width136}>
                        {extension === pdf ? (
                          isOpenLoading ? (
                            <div className={styles.height40}>
                              <LottieContainer
                                animationData={animationData}
                                segments={isOpenLoading ? [1, 180] : [180, 270]}
                                loop={isOpenLoading}
                                width={28}
                                height={28}
                              />
                            </div>
                          ) : (
                            <div
                              className={styles.openButton}
                              data-test-id={'open-button'}
                              onClick={() => {
                                addProcessingLink(openLink);
                                downloadOrOpenBlobFile(fileLink, '', true)
                                  .catch(showTooltip)
                                  .finally(() =>
                                    removeProcessingLink(openLink),
                                  );
                              }}
                            >
                              <ActionText text={i18nCommon.t('open')} />
                              <ArrowRight />
                            </div>
                          )
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {Object.entries(searchResult.matchedInnerFields).map(
                    (item) => (
                      <div
                        className={styles.rowContainer}
                        key={`${searchResult.pageName}-${
                          searchResult.foundSearchValue ?? ''
                        }-${item.at(0) ?? ''}`}
                      >
                        <div className={styles.greyText}>
                          {highlightMatches(
                            item.at(0) ?? '',
                            params.searchValue ?? '',
                            styles.highlight,
                          )}
                        </div>
                        <div
                          data-test-id={'found-inner-fields'}
                          className={styles.greyText}
                        >
                          :{' '}
                          {highlightMatches(
                            item.at(1) ?? '',
                            params.searchValue ?? '',
                            styles.highlight,
                          )}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              );
            })}
            {index + 1 === searchData.data?.data.length && (
              <div ref={lastRowRef} className={styles.lastRow} />
            )}
          </div>
        ))}
        {isPlaceholderVisible && (
          <TablePlaceholder
            containerClassname={styles.marginTop158}
            customContent={
              <div
                data-test-id={'placeholder'}
                className={styles.placeholderContainer}
              >
                <div className={clsx(styles.title, styles.marginBottom12)}>
                  {i18n.t('placeholder_title')}
                </div>
                <div>{i18n.t('placeholder_text')}</div>
              </div>
            }
          />
        )}
        {isInterruptModalVisible && (
          <CustomModal
            isOpen={true}
            submitButtonTestId={'interrupt-button'}
            onSubmit={() => {
              closeInterruptModal();
              void form.handleSubmitDefault();
            }}
            onCancel={closeInterruptModal}
            cancelButtonText={i18n.t('cancel')}
            submitButtonText={i18n.t('continue')}
            titleText={i18n.t('interrupt_search_modal_title')}
            bodyText={i18n.t('interrupt_search_modal_text')}
            containerClassName={styles.modalBodyMinWidth480}
            textClassName={styles.lineHeight24}
            submitButtonType={ButtonColor.Primary}
          />
        )}
        {isTooltipVisible && (
          <Tooltip
            isError={true}
            closeTooltip={closeTooltip}
            bodyContent={
              <Trans
                i18nKey={'fileNotFound'}
                components={{
                  supportLink: <a href={`mailto:${mail}`} />,
                }}
              />
            }
            className={styles.errorTooltip}
            showCancelButton={true}
            iconClassName={styles.errorTooltipIcon}
          />
        )}
      </Loading>
    </div>
  );
};
